import type { GrowthBook } from '@growthbook/growthbook-react';
import { useCallback, useMemo } from 'react';
import { getWildcardPattern, toSet } from '../../hooks/useEmailAllowlist';
import { generateHashEmail } from '../../lib/generateHashEmail';

export type GrowthbookPermissionOptions = {
  emptyAllowlistMeansEnabled?: boolean;
};

export type IsGrowthbookFeatureOnFn = (
  flagName: string,
  customStr?: string,
  options?: GrowthbookPermissionOptions
) => boolean;

export type UseGrowthBookPermissions = (params: {
  userEmail: string | undefined;
  growthBookSalt: string;
  growthBook: GrowthBook;
}) => {
  isGrowthbookFeatureOn: IsGrowthbookFeatureOnFn;
};

export const useGrowthBookPermissions: UseGrowthBookPermissions = ({ userEmail, growthBookSalt, growthBook }) => {
  const userEmailHash = useMemo<string | null>(
    () => (userEmail ? generateHashEmail(userEmail, growthBookSalt) : null),
    [userEmail, growthBookSalt]
  );

  const userEmailWildcardHash = useMemo<string | null>(() => {
    if (!userEmail) return null;
    const wildCard = getWildcardPattern(userEmail);

    return wildCard ? generateHashEmail(wildCard, growthBookSalt) : null;
  }, [userEmail, growthBookSalt]);

  const isGrowthbookFeatureOn = useCallback<IsGrowthbookFeatureOnFn>(
    (flagName, customStr, options) => {
      if (growthBook?.ready) {
        // noop - DO NOT REMOVE - it exists here to satisfy callback dependency array
      }

      const flagValue = growthBook.getFeatureValue<boolean | string | null>(flagName, null);

      if (typeof flagValue === 'boolean') {
        return flagValue;
      }

      if (typeof flagValue !== 'string') {
        return false;
      }

      if (options?.emptyAllowlistMeansEnabled && flagValue.trim().length === 0) {
        return true;
      }

      const flagValueSet = toSet(flagValue);

      if (customStr) {
        return flagValueSet.has(generateHashEmail(customStr, growthBookSalt));
      }

      return !!(
        (userEmailHash && flagValueSet.has(userEmailHash)) ||
        (userEmailWildcardHash && flagValueSet.has(userEmailWildcardHash))
      );
    },
    [growthBook, growthBookSalt, userEmailHash, userEmailWildcardHash, growthBook?.ready]
  );

  return { isGrowthbookFeatureOn: isGrowthbookFeatureOn };
};
